import { useCallback } from "react";
import type { TFunction } from "i18next";
import {
  useCreateSigningOperationMutation,
  useLaunchSigningOperationMutation,
  useStartDomainMobileSigningMutation,
} from "../api/mobileSigning";
import type { MobileLaunch, SigningOperationWithLinks } from "../types/mobileSigning";
import { useEgovMobileSigning } from "./useEgovMobileSigning";
import { useOrderSigning } from "./useOrderSigning";

interface PromptState {
  title: string;
  text: string;
  variant: "success" | "error";
}

interface UseDocumentSigningOptions {
  previewFn: () => Promise<Blob>;
  signFn: (cmsSignature: string, fileBase64?: string) => Promise<unknown>;
  onSuccess: () => void;
  setPrompt: (prompt: PromptState) => void;
  t: TFunction;
  skipFileUpload?: boolean;
  resolveErrorText?: (error: unknown) => string | null;
  /** Domain launch endpoint — first launch, document must still be a draft. */
  mobileUrl?: string | null;
  /** For documents the backend cannot regenerate: the launch endpoint reads the
   * stored preview, so it has to exist before the QR session opens. */
  ensurePreviewBeforeMobileLaunch?: boolean;
  /** Resume/retry path: create the operation, then claim its links. */
  mobileLaunchFn?: () => Promise<MobileLaunch | SigningOperationWithLinks>;
  /** Lets an already-registered document start a fresh session on retry. */
  signingDocumentPublicId?: string | null;
  onMobileCancel?: (operationPublicId: string) => Promise<unknown>;
  onSessionChange?: (isActive: boolean) => void;
  /** The organization signs with a business certificate, workers with a personal one. */
  egovVariant?: "business" | "personal";
}

/**
 * Pairs the existing NCALayer flow with eGov Mobile so a call site can render
 * both buttons with a three-line diff.
 */
export function useDocumentSigning(options: UseDocumentSigningOptions) {
  const [startDomainMobileSigning] = useStartDomainMobileSigningMutation();
  const [createSigningOperation] = useCreateSigningOperationMutation();
  const [launchSigningOperation] = useLaunchSigningOperationMutation();

  const nca = useOrderSigning({
    previewFn: options.previewFn,
    signFn: options.signFn,
    onSuccess: options.onSuccess,
    setPrompt: options.setPrompt,
    t: options.t,
    skipFileUpload: options.skipFileUpload,
    signingDocumentPublicId: options.signingDocumentPublicId,
    resolveErrorText: options.resolveErrorText,
  });

  const { mobileLaunchFn, mobileUrl, previewFn, ensurePreviewBeforeMobileLaunch } = options;

  const launchFn = useCallback(
    async function launchFn() {
      if (mobileLaunchFn) return mobileLaunchFn();
      if (!mobileUrl) {
        throw new Error("Mobile signing is not available for this document");
      }
      if (ensurePreviewBeforeMobileLaunch) await previewFn();
      return startDomainMobileSigning({ url: mobileUrl }).unwrap();
    },
    [
      mobileLaunchFn,
      mobileUrl,
      previewFn,
      ensurePreviewBeforeMobileLaunch,
      startDomainMobileSigning,
    ],
  );

  const { signingDocumentPublicId } = options;

  // A registered document cannot go through the domain endpoint again — that
  // one only accepts drafts. Retrying opens a new operation on the existing
  // signing document instead.
  const resumeFn = useCallback(
    function resumeFn() {
      if (!signingDocumentPublicId) return launchFn();
      return createSigningOperation({ signingDocumentId: signingDocumentPublicId })
        .unwrap()
        .then(function claimLinks(operation) {
          return launchSigningOperation(operation.operation_public_id).unwrap();
        });
    },
    [signingDocumentPublicId, launchFn, createSigningOperation, launchSigningOperation],
  );

  const mobile = useEgovMobileSigning({
    launchFn,
    resumeFn,
    cancelFn: options.onMobileCancel,
    onSuccess: options.onSuccess,
    setPrompt: options.setPrompt,
    t: options.t,
    onSessionChange: options.onSessionChange,
    preferBusiness: (options.egovVariant ?? "business") === "business",
  });

  const isMobileAvailable = Boolean(
    options.mobileUrl || options.mobileLaunchFn || signingDocumentPublicId,
  );

  return {
    nca,
    mobile,
    egovVariant: options.egovVariant ?? "business",
    isMobileAvailable,
    isBusy: nca.isLoading || mobile.isStarting || mobile.isActive,
  };
}
